import { useEffect } from "react";
import {
  ArrowRight, BellRing, Bookmark, CalendarDays, CheckCircle2, Circle,
  Clock3, FileText, ListTodo, Plus, Search, Sparkles, StickyNote, Target,
  Zap, ShieldCheck, UsersRound
} from "lucide-react";
import SiteHeader from "./SiteHeader";
import SiteFooter from "./SiteFooter";

const tasks = [
  ["Finalize launch checklist", "Today", "High"],
  ["Review campaign performance", "10:30 AM", "Medium"],
  ["Prepare client presentation", "Tomorrow", "Medium"],
];

function WorkspacePreview({ compact = false }) {
  return (
    <div className={`msp-app ${compact ? "msp-app--compact" : ""}`}>
      <aside className="msp-app-sidebar">
        <div className="msp-mini-brand"><img src="/1.png" alt="" /><b>Cullath</b></div>
        <span className="active"><Sparkles size={15}/> My Space</span>
        <span><ListTodo size={15}/> Tasks</span>
        <span><StickyNote size={15}/> Notes</span>
        <span><Bookmark size={15}/> Bookmarks</span>
        <span><BellRing size={15}/> Reminders</span>
      </aside>
      <div className="msp-app-main">
        <div className="msp-app-top"><div><small>MY SPACE</small><strong>Good morning, Alex</strong></div><div className="msp-search"><Search size={14}/> Search</div></div>
        <div className="msp-metrics">
          <article><span><ListTodo size={16}/></span><div><b>12</b><small>Open tasks</small></div></article>
          <article><span><Target size={16}/></span><div><b>5</b><small>Priorities</small></div></article>
          <article><span><BellRing size={16}/></span><div><b>3</b><small>Reminders</small></div></article>
        </div>
        <div className="msp-preview-grid">
          <section className="msp-task-panel">
            <header><b>Today</b><button><Plus size={13}/> Add task</button></header>
            {tasks.map(([name,time,priority], index) => <div className="msp-task-row" key={name}>
              {index === 2 ? <CheckCircle2 size={17}/> : <Circle size={17}/>}<div><b>{name}</b><small><Clock3 size={11}/>{time}</small></div><em>{priority}</em>
            </div>)}
          </section>
          <section className="msp-note-panel">
            <header><b>Quick notes</b><Plus size={15}/></header>
            <div className="msp-sticky msp-sticky--one"><b>Campaign ideas</b><p>Customer story, product teaser, behind the scenes.</p></div>
            <div className="msp-sticky msp-sticky--two"><b>Remember</b><p>Send revised proposal before 4 PM.</p></div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default function MySpacePage(){
  useEffect(() => { window.scrollTo(0,0); }, []);
  return <div className="msp-page">
    <SiteHeader isProductPage lightProductHero />
    <main>
      <section className="msp-hero">
        <div className="msp-orbit msp-orbit--one"/><div className="msp-orbit msp-orbit--two"/>
        <div className="page-container msp-hero-grid">
          <div className="msp-hero-copy">
            <div className="msp-kicker"><Sparkles size={14}/> CULLATH MY SPACE</div>
            <h1>Your personal workspace, <span>beautifully organized.</span></h1>
            <p>Bring tasks, notes, bookmarks, reminders, and daily priorities into one calm space—so important work never gets lost between conversations.</p>
            <div className="msp-actions"><a className="msp-primary" href="#get-started">Explore My Space <ArrowRight size={17}/></a><a className="msp-secondary" href="#features">See Features</a></div>
            <div className="msp-trust"><span><ShieldCheck size={15}/> Secure by design</span><span><Zap size={15}/> Quick to use</span><span><UsersRound size={15}/> Built for every role</span></div>
          </div>
          <div className="msp-hero-visual"><WorkspacePreview/><div className="msp-float msp-float--one"><CheckCircle2 size={17}/><span><b>Task completed</b><small>Launch checklist updated</small></span></div><div className="msp-float msp-float--two"><BellRing size={17}/><span><b>Reminder</b><small>Client call in 15 minutes</small></span></div></div>
        </div>
      </section>

      <section className="msp-section" id="features"><div className="page-container">
        <div className="msp-heading"><span>EVERYTHING IN ONE VIEW</span><h2>Make room for <em>focused work.</em></h2><p>My Space turns small personal actions into a clear daily system without adding another disconnected app.</p></div>
        <div className="msp-feature-grid">
          {[
            [ListTodo,"Priority Tasks","Organize daily work, mark urgency, and keep your next action visible."],
            [StickyNote,"Quick Notes","Capture ideas and working notes the moment they appear."],
            [Bookmark,"Smart Bookmarks","Save important messages, pages, and context for easy return."],
            [BellRing,"Useful Reminders","Bring time-sensitive follow-up back exactly when it matters."],
            [CalendarDays,"Daily Planning","See today’s workload and shape a realistic plan in one place."],
            [Sparkles,"Clean Personal View","Stay focused with a calm interface designed around your priorities."],
          ].map(([Icon,title,copy]) => <article key={title}><span><Icon size={21}/></span><h3>{title}</h3><p>{copy}</p><a href="#workflow">Learn more <ArrowRight size={14}/></a></article>)}
        </div>
      </div></section>

      <section className="msp-flow" id="workflow"><div className="page-container msp-flow-grid">
        <div className="msp-flow-copy"><span>YOUR DAY, SIMPLIFIED</span><h2>From scattered thoughts to <em>clear progress.</em></h2><p>Capture what matters, decide what happens next, and return to one trusted view throughout your day.</p>
          <div className="msp-steps">
            <article><i>1</i><div><b>Capture instantly</b><p>Add a task, note, bookmark, or reminder before the thought disappears.</p></div></article>
            <article><i>2</i><div><b>Organize with purpose</b><p>Set priority, timing, and context so every item has a useful next step.</p></div></article>
            <article><i>3</i><div><b>Finish with focus</b><p>Work from a clean daily view and close the loop as items are completed.</p></div></article>
          </div>
        </div>
        <div className="msp-flow-visual"><WorkspacePreview compact /></div>
      </div></section>

      <section className="msp-benefits"><div className="page-container"><div className="msp-heading"><span>BUILT FOR BETTER DAYS</span><h2>Less mental clutter. <em>More momentum.</em></h2></div><div className="msp-benefit-grid">
        <article><Zap/><h3>Faster follow-through</h3><p>Turn quick thoughts into visible actions before they are forgotten.</p></article>
        <article><Target/><h3>Clear priorities</h3><p>Know what deserves attention now and what can wait until later.</p></article>
        <article><FileText/><h3>Context stays close</h3><p>Keep useful notes and bookmarks beside the work they support.</p></article>
        <article><CheckCircle2/><h3>A calmer finish</h3><p>See progress clearly and end the day knowing what is complete.</p></article>
      </div></div></section>

      <section className="msp-cta" id="get-started"><div className="page-container"><div className="msp-cta-card"><div><span>GET STARTED WITH MY SPACE</span><h2>Give every important thought a place to move forward.</h2><p>One personal workspace for the tasks, notes, reminders, and details that keep your day moving.</p><a href="#product">Start with My Space <ArrowRight size={17}/></a></div><div className="msp-cta-art"><div className="msp-cta-note"><StickyNote/><b>Idea saved</b><small>New campaign concept</small></div><div className="msp-cta-task"><CheckCircle2/><b>3 priorities complete</b></div></div></div></div></section>
    </main>
    <SiteFooter/>
  </div>
}
